import { FUtil, FServiceAPI, FI18n } from '@freelog/tools-lib';
import { ResourceVersionCreatorPageModelState } from '@/models/resourceVersionCreatorPage';
import moment, { Moment } from 'moment';
import fMessage from '@/components/fMessage';

type MetaInfoArray_Type = {
  insertMode: 1 | 2;
  key: string;
  name: string;
  remark: string;
  value: number | string | null;
  valueDisplay: string;
  valueUnit: string;
}[];

interface FileInfo {
  sha1: string;
  state: 'success' | 'fail' | 'nonentity';
  fileSize: number;
  info: MetaInfoArray_Type;
}

interface GetFileInfosBySha1Params {
  sha1: string[];
  resourceTypeCode: string;
}

export async function getFilesSha1Info(
  { sha1, resourceTypeCode }: GetFileInfosBySha1Params,
  cdPartially: (s: any[]) => void = () => undefined,
): Promise<{
  error: string;
  result: FileInfo[];
}> {
  if (sha1.length === 0) {
    return {
      error: '',
      result: [],
    };
  }

  let delay: number = 500;

  let needHandleSha1: string[] = [...sha1];

  let allData: FileInfo[] = [];

  while (true) {
    const {
      ret,
      errCode,
      data,
      msg,
    }: {
      ret: number;
      errCode: number;
      msg: string;
      data: {
        sha1: string;
        fileSize: number;
        metaAnalyzeStatus: number;
        metaInfoArray: FileInfo['info'];
      }[];
    } = await FServiceAPI.Storage.filesListInfo({
      sha1: needHandleSha1.join(','),
      resourceTypeCode: resourceTypeCode,
    });

    if (ret !== 0 || errCode !== 0) {
      return {
        error: msg,
        result: allData,
      };
    }

    needHandleSha1 = data
      .filter((d) => {
        return d.metaAnalyzeStatus === 0 || d.metaAnalyzeStatus === 1;
      })
      .map((d) => {
        return d.sha1;
      });
    const finishedInfo: FileInfo[] = data
      .filter((d) => {
        return d.metaAnalyzeStatus !== 0 && d.metaAnalyzeStatus !== 1;
      })
      .map((d) => {
        let state: 'success' | 'fail' | 'nonentity' = 'fail';
        if (!d.metaAnalyzeStatus) {
          state = 'nonentity';
        } else if (d.metaAnalyzeStatus === 2) {
          state = 'success';
        } else if (d.metaAnalyzeStatus === 3) {
          state = 'fail';
        }
        // console.log(d, '90wieojiksdjf;lkasdjf;lksdjflksjdflkjsdlfkjsdlkj');
        return {
          sha1: d.sha1,
          state,
          fileSize: d.fileSize,
          info: d.metaInfoArray,
          keySort: [],
        };
      });
    cdPartially && cdPartially(finishedInfo);
    allData = [...allData, ...finishedInfo];

    if (needHandleSha1.length === 0) {
      break;
    }
    await FUtil.Tool.promiseSleep(delay);
  }
  return {
    error: '',
    result: allData,
  };
}

interface HandleData_By_Sha1_And_ResourceTypeCode_And_InheritData_Params {
  sha1: string;
  resourceTypeCode: string;
  inheritData: {
    additionalProperties: {
      key: string;
      name: string;
      value: string;
      description: string;
    }[];
    customProperties: {
      key: string;
      name: string;
      value: string;
      description: string;
    }[];
    customConfigurations: {
      key: string;
      name: string;
      description: string;
      type: 'input' | 'select';
      input: string;
      select: string[];
    }[];
  };
}

interface HandleData_By_Sha1_And_ResourceTypeCode_And_InheritData_Return {
  state: 'failed' | 'success';
  failedMsg: string;
  rawProperties: {
    key: string;
    name: string;
    value: string;
    description: string;
  }[];
  additionalProperties: {
    key: string;
    name: string;
    value: string;
    description: string;
  }[];
  customProperties: {
    key: string;
    name: string;
    value: string;
    description: string;
  }[];
  customConfigurations: {
    key: string;
    name: string;
    description: string;
    type: 'input' | 'select';
    input: string;
    select: string[];
  }[];
  keysSort: string[];
}

export async function handleData_By_Sha1_And_ResourceTypeCode_And_InheritData({
  sha1,
  resourceTypeCode,
  inheritData,
}: HandleData_By_Sha1_And_ResourceTypeCode_And_InheritData_Params): Promise<HandleData_By_Sha1_And_ResourceTypeCode_And_InheritData_Return> {
  let metaInfoArray: MetaInfoArray_Type = [];

  if (sha1 !== '') {
    const params0: Parameters<typeof getFilesSha1Info>[0] = {
      sha1: [sha1],
      resourceTypeCode: resourceTypeCode,
    };
    const { result, error }: Awaited<ReturnType<typeof getFilesSha1Info>> =
      await getFilesSha1Info(params0);

    if (error !== '') {
      return {
        state: 'failed',
        failedMsg: error,
        rawProperties: [],
        additionalProperties: [],
        customProperties: [],
        customConfigurations: [],
        keysSort: [],
      };
    }

    if (result[0].state === 'fail') {
      metaInfoArray = result[0].info;
    } else if (result[0].state === 'success') {
      metaInfoArray = result[0].info;
    } else {
      return {
        state: 'failed',
        failedMsg: '未知原因',
        rawProperties: [],
        additionalProperties: [],
        customProperties: [],
        customConfigurations: [],
        keysSort: [],
      };
    }
  } else {
    const {
      ret,
      errCode,
      msg,
      data,
    }: {
      ret: number;
      errCode: number;
      msg: string;
      data: {
        metaInfoArray: MetaInfoArray_Type;
      };
    } = await FServiceAPI.Storage.filesInfo({
      resourceTypeCode: resourceTypeCode,
    });

    if (ret !== 0 || errCode !== 0) {
      fMessage(msg, 'error');
      return {
        state: 'failed',
        failedMsg: '未知原因',
        rawProperties: [],
        additionalProperties: [],
        customProperties: [],
        customConfigurations: [],
        keysSort: [],
      };
    }
    metaInfoArray = data.metaInfoArray;
  }

  let additionalProperties_availableData = [
    ...inheritData.additionalProperties,
    ...inheritData.customProperties,
  ];

  const rawProperties: HandleData_By_Sha1_And_ResourceTypeCode_And_InheritData_Return['rawProperties'] =
    metaInfoArray
      .filter((i) => {
        return i.insertMode === 1 && !!i.valueDisplay;
      })
      .map<
        HandleData_By_Sha1_And_ResourceTypeCode_And_InheritData_Return['rawProperties'][number]
      >((i) => {
        return {
          key: i.key,
          name: i.name,
          value: i.valueDisplay,
          description: i.remark,
        };
      });
  const rawProperties_allKeys: string[] = rawProperties.map((i) => {
    return i.key;
  });
  const rawProperties_allNames: string[] = rawProperties.map((i) => {
    return i.name;
  });

  const additionalProperties: HandleData_By_Sha1_And_ResourceTypeCode_And_InheritData_Return['additionalProperties'] =
    metaInfoArray
      .filter((i) => {
        return i.insertMode === 2;
      })
      .map<
        HandleData_By_Sha1_And_ResourceTypeCode_And_InheritData_Return['additionalProperties'][number]
      >((i) => {
        return {
          key: i.key,
          name: i.name,
          value: i.valueDisplay,
          description: i.remark,
        };
      })
      .filter((i) => {
        return (
          !rawProperties_allKeys.includes(i.key) &&
          !rawProperties_allNames.includes(i.name)
        );
      })
      .map((i) => {
        const item = additionalProperties_availableData.find((ap) => {
          return ap.key === i.key;
        });

        let value: string = i.value;
        if (item) {
          additionalProperties_availableData =
            additionalProperties_availableData.filter((ad) => {
              return ad.key !== i.key;
            });
          value = item.value;
        } else {
          const item2 = additionalProperties_availableData.find((ap) => {
            return ap.name === i.name;
          });

          if (item2) {
            additionalProperties_availableData =
              additionalProperties_availableData.filter((ad) => {
                return ad.name !== i.name;
              });
            value = item2.value;
          }
        }

        return {
          key: i.key,
          name: i.name,
          value: value,
          description: i.description,
        };
      });

  for (const i of additionalProperties) {
    i.value = (
      await correct_Resource_AdditionalProperties({
        key: i.key,
        value: i.value,
      })
    ).value;
  }
  const additionalProperties_allKeys: string[] = additionalProperties.map(
    (i) => {
      return i.key;
    },
  );
  const additionalProperties_allNames: string[] = additionalProperties.map(
    (i) => {
      return i.name;
    },
  );

  const customProperties: HandleData_By_Sha1_And_ResourceTypeCode_And_InheritData_Return['customProperties'] =
    inheritData.customProperties.filter((cp) => {
      return (
        !rawProperties_allKeys.includes(cp.key) &&
        !rawProperties_allNames.includes(cp.name) &&
        !additionalProperties_allKeys.includes(cp.key) &&
        !additionalProperties_allNames.includes(cp.name)
      );
    });

  const customProperties_allKeys: string[] = customProperties.map((i) => {
    return i.key;
  });
  const customProperties_allNames: string[] = customProperties.map((i) => {
    return i.name;
  });

  const customConfigurations: HandleData_By_Sha1_And_ResourceTypeCode_And_InheritData_Return['customConfigurations'] =
    inheritData.customConfigurations.filter((cc) => {
      return (
        !rawProperties_allKeys.includes(cc.key) &&
        !rawProperties_allNames.includes(cc.name) &&
        !additionalProperties_allKeys.includes(cc.key) &&
        !additionalProperties_allNames.includes(cc.name) &&
        !customProperties_allKeys.includes(cc.key) &&
        !customProperties_allNames.includes(cc.name)
      );
    });

  return {
    state: 'success',
    failedMsg: '',
    rawProperties: rawProperties,
    additionalProperties: additionalProperties,
    customProperties: customProperties,
    customConfigurations: customConfigurations,
    keysSort: metaInfoArray.map((d) => {
      return d.key;
    }),
  };
}

interface HandleData_By_Sha1_And_ResourceTypeCode_And_InheritData2_Params {
  sha1: string;
  resourceTypeCode: string;
  inheritData: {
    additionalProperties: {
      key: string;
      value: string;
    }[];
    customProperties: {
      key: string;
      name: string;
      value: string;
      description: string;
    }[];
    customConfigurations: {
      key: string;
      name: string;
      description: string;
      type: 'input' | 'select';
      input: string;
      select: string[];
    }[];
  };
}

interface HandleData_By_Sha1_And_ResourceTypeCode_And_InheritData2_Return {
  sha1: string;
  resourceTypeCode: string;
  state: 'failed' | 'success';
  failedMsg: string;
  systemProperties: {
    key: string;
    name: string;
    value: string;
    description: string;
    type: 'raw' | 'additional';
    valueConfig?: {
      text?: {
        nullable: boolean;
        minLength: number;
        maxLength: number;
      };
      textArea?: {
        nullable: boolean;
        minLength: number;
        maxLength: number;
      };
      integer?: {
        nullable: boolean;
        min: number;
        max: number;
      };
      decimal?: {
        nullable: boolean;
        minDecimal: number;
        maxDecimal: number;
        precision: number;
      };
      date?: {
        nullable: boolean;
        startDate: string;
        limitDate: string;
      };
      dataTime?: {
        nullable: boolean;
        startDateTime: string;
        limitDateTime: string;
      };
      configText?: {
        nullable: boolean;
        defaultValue: string;
        minLength: number;
        maxLength: number;
      };
      configEnum?: {
        nullable: boolean;
        defaultValue: string;
        options: string[];
      };
    };
  }[];
  customProperties: {
    key: string;
    name: string;
    value: string;
    description: string;
  }[];
  customConfigurations: {
    key: string;
    name: string;
    description: string;
    type: 'input' | 'select';
    input: string;
    select: string[];
  }[];
}

export async function handleData_By_Sha1_And_ResourceTypeCode_And_InheritData2({
  sha1,
  resourceTypeCode,
  inheritData,
}: HandleData_By_Sha1_And_ResourceTypeCode_And_InheritData2_Params): Promise<HandleData_By_Sha1_And_ResourceTypeCode_And_InheritData2_Return> {
  let metaInfoArray: MetaInfoArray_Type = [];

  if (sha1 !== '') {
    const params0: Parameters<typeof getFilesSha1Info>[0] = {
      sha1: [sha1],
      resourceTypeCode: resourceTypeCode,
    };
    const { result, error }: Awaited<ReturnType<typeof getFilesSha1Info>> =
      await getFilesSha1Info(params0);

    if (error !== '') {
      return {
        sha1: sha1,
        resourceTypeCode: resourceTypeCode,
        state: 'failed',
        failedMsg: error,
        systemProperties: [],
        customProperties: [],
        customConfigurations: [],
      };
    }

    if (result[0].state === 'fail') {
      metaInfoArray = result[0].info;
    } else if (result[0].state === 'success') {
      metaInfoArray = result[0].info;
    } else {
      return {
        sha1: sha1,
        resourceTypeCode: resourceTypeCode,
        state: 'failed',
        failedMsg: '未知原因',
        systemProperties: [],
        customProperties: [],
        customConfigurations: [],
      };
    }
  } else {
    const {
      ret,
      errCode,
      msg,
      data,
    }: {
      ret: number;
      errCode: number;
      msg: string;
      data: {
        metaInfoArray: MetaInfoArray_Type;
      };
    } = await FServiceAPI.Storage.filesInfo({
      resourceTypeCode: resourceTypeCode,
    });

    if (ret !== 0 || errCode !== 0) {
      fMessage(msg, 'error');
      return {
        sha1: sha1,
        resourceTypeCode: resourceTypeCode,
        state: 'failed',
        failedMsg: '未知原因',
        systemProperties: [],
        customProperties: [],
        customConfigurations: [],
      };
    }
    metaInfoArray = data.metaInfoArray;
  }

  const keys: string[] = metaInfoArray
    .filter((i) => {
      return i.insertMode === 2;
    })
    .map((i) => {
      return i.key;
    });

  const results: GetAttrsInfoByKeysReturn = await getAttrsInfoByKeys({
    keys: keys,
  });

  const systemProperties: HandleData_By_Sha1_And_ResourceTypeCode_And_InheritData2_Return['systemProperties'] =
    metaInfoArray
      .map<
        HandleData_By_Sha1_And_ResourceTypeCode_And_InheritData2_Return['systemProperties'][number]
      >((i: MetaInfoArray_Type[number]) => {
        if (i.insertMode === 1) {
          return {
            key: i.key,
            name: i.name,
            value: i.valueDisplay,
            description: i.remark,
            type: 'raw',
            valueConfig: {},
          };
        }

        const result: GetAttrsInfoByKeysReturn[number] | undefined =
          results.find((j) => {
            return i.key === j.key;
          });
        // enum FormatEnum {
        //   date = 4,
        //   dataTime = 5,
        //   textInput = 6,
        //   textArea = 7,
        //   integer = 8,
        //   decimal = 9,
        // }
        // | { startDate?: string; limitDate?: string } // 4：日期
        // | { startDateTime?: string; limitDateTime?: string } // 5：日期和时间
        // | { minLength?: number; maxLength?: number } // 6：文本  7: 多行文本
        // | { min?: number; max?: number } // 8: 整数
        // | { minDecimal?: number; maxDecimal?: number; precision?: number }; // 9: 小数
        // const valueConfig: HandleData_By_Sha1_And_ResourceTypeCode_And_InheritData2_Return['systemProperties'][number]['valueConfig'] =
        //   {};
        return {
          key: i.key,
          name: i.name,
          value:
            inheritData.additionalProperties.find((ap) => {
              return ap.key === i.key;
            })?.value ||
            inheritData.customProperties.find((cp) => {
              return cp.key === i.key;
            })?.value ||
            '',
          description: i.remark,
          type: 'additional',
          valueConfig: result?.valueConfig,
        };
      })
      .filter((i) => {
        return !(i.type === 'raw' && i.value === '');
      });
  const systemProperties_allKeys: string[] = systemProperties.map((i) => {
    return i.key;
  });
  const systemProperties_allNames: string[] = systemProperties.map((i) => {
    return i.name;
  });

  const customProperties: HandleData_By_Sha1_And_ResourceTypeCode_And_InheritData2_Return['customProperties'] =
    inheritData.customProperties.filter((cp) => {
      return (
        !systemProperties_allKeys.includes(cp.key) &&
        !systemProperties_allNames.includes(cp.name)
      );
    });

  const customProperties_allKeys: string[] = customProperties.map((i) => {
    return i.key;
  });
  const customProperties_allNames: string[] = customProperties.map((i) => {
    return i.name;
  });

  const customConfigurations: HandleData_By_Sha1_And_ResourceTypeCode_And_InheritData2_Return['customConfigurations'] =
    inheritData.customConfigurations.filter((cc) => {
      return (
        !systemProperties_allKeys.includes(cc.key) &&
        !systemProperties_allNames.includes(cc.name) &&
        !customProperties_allKeys.includes(cc.key) &&
        !customProperties_allNames.includes(cc.name)
      );
    });

  return {
    sha1: sha1,
    resourceTypeCode: resourceTypeCode,
    state: 'success',
    failedMsg: '',
    systemProperties: systemProperties,
    customProperties: customProperties,
    customConfigurations: customConfigurations,
  };
}

interface Correct_Resource_AdditionalProperties_Params {
  key: string;
  value: string;
}

async function correct_Resource_AdditionalProperties({
  key,
  value,
}: Correct_Resource_AdditionalProperties_Params): Promise<{
  key: string;
  value: string;
}> {
  const params: Parameters<typeof FServiceAPI.Resource.getAttrsInfoByKey>[0] = {
    key: key,
  };

  const {
    ret,
    errCode,
    msg,
    data,
  }: {
    ret: number;
    errCode: number;
    msg: string;
    data: null | {
      format: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9; //	值格式 1：文本 2：数值 3：时间 4：日期 5：日期和时间 6: 单行文本 7: 多行文本 8: 整数 9: 小数
      contentRule?:
        | { startDate?: string; limitDate?: string } // 4：日期
        | { startDateTime?: string; limitDateTime?: string } // 5：日期和时间
        | { minLength?: number; maxLength?: number } // 6：文本  7: 多行文本
        | { min?: number; max?: number } // 8: 整数
        | { minDecimal?: number; maxDecimal?: number; precision?: number }; // 9: 小数
    };
  } = await FServiceAPI.Resource.getAttrsInfoByKey(params);

  if (data) {
    const contentRule: any = data.contentRule;

    // 1：文本
    if (data.format === 1) {
      if (
        value.length < (contentRule?.minLength || 0) ||
        value.length > (contentRule?.maxLength || 140)
      ) {
        return {
          key,
          value: '',
        };
      }
    }

    // 2：数值
    if (data.format === 2) {
      if (
        !new RegExp(`^-?\\d+(\\.\\d{0,${Number.MAX_SAFE_INTEGER}})?$`).test(
          value,
        ) ||
        Number(value) < (contentRule?.min || Number.MAX_SAFE_INTEGER) ||
        Number(value) > (contentRule?.max || Number.MAX_SAFE_INTEGER)
      ) {
        return {
          key,
          value: '',
        };
      }
    }

    // 3：时间
    if (data.format === 3) {
      const momentValue: Moment = moment(value, 'HH:mm:ss');
      if (
        !momentValue.isValid() ||
        momentValue.format('HH:mm:ss') !== value ||
        (contentRule?.startDateTime &&
          momentValue.isBefore(contentRule?.startDateTime)) ||
        (contentRule?.limitDateTime &&
          momentValue.isAfter(contentRule?.limitDateTime))
      ) {
        return {
          key,
          value: '',
        };
      }
    }

    // 4：日期
    if (data.format === 4) {
      const momentValue: Moment = moment(value, 'YYYY-MM-DD');
      if (
        !momentValue.isValid() ||
        momentValue.format('YYYY-MM-DD') !== value ||
        (contentRule?.startDateTime &&
          momentValue.isBefore(contentRule?.startDateTime)) ||
        (contentRule?.limitDateTime &&
          momentValue.isAfter(contentRule?.limitDateTime))
      ) {
        return {
          key,
          value: '',
        };
      }
    }

    // 5：日期和时间
    if (data.format === 5) {
      const momentValue: Moment = moment(value, 'YYYY-MM-DD HH:mm:ss');
      if (
        !momentValue.isValid() ||
        momentValue.format('YYYY-MM-DD HH:mm:ss') !== value ||
        (contentRule?.startDateTime &&
          momentValue.isBefore(contentRule?.startDateTime)) ||
        (contentRule?.limitDateTime &&
          momentValue.isAfter(contentRule?.limitDateTime))
      ) {
        return {
          key,
          value: '',
        };
      }
    }

    // 6: 单行文本
    if (data.format === 6) {
      if (
        value.length < (contentRule?.minLength || 0) ||
        value.length > (contentRule?.maxLength || 140)
      ) {
        return {
          key,
          value: '',
        };
      }
    }

    // 7: 多行文本
    if (data.format === 7) {
      if (
        value.length < (contentRule?.minLength || 0) ||
        value.length > (contentRule?.maxLength || 140)
      ) {
        return {
          key,
          value: '',
        };
      }
    }

    // 8: 整数
    if (data.format === 8) {
      if (
        !new RegExp(/^-?\d+$/).test(value) ||
        Number(value) < (contentRule?.min || Number.MIN_SAFE_INTEGER) ||
        Number(value) > (contentRule?.max || Number.MAX_SAFE_INTEGER)
      ) {
        return {
          key,
          value: '',
        };
      }
    }

    // 9: 小数
    if (data.format === 9) {
      // if (!new RegExp('^-?\\d+(\\.\\d{0,' + (contentRule?.minDecimal || Number.MAX_SAFE_INTEGER) + '})?$').test(value)
      if (
        !new RegExp(
          `^-?\\d+(\\.\\d{0,${
            contentRule?.minDecimal || Number.MAX_SAFE_INTEGER
          }})?$`,
        ).test(value) ||
        Number(value) < (contentRule?.min || Number.MIN_SAFE_INTEGER) ||
        Number(value) > (contentRule?.max || Number.MAX_SAFE_INTEGER)
      ) {
        return {
          key,
          value: '',
        };
      }
    }
  }

  return {
    key,
    value,
  };
}

// export function resourceNameOptimized(name: string): string {
//   return name.replace(new RegExp(/(\\|\/|:|\*|\?|"|<|>|\||\s|@|\$|#|([\u{1F600}-\u{1F64F}|\u{1F300}-\u{1F5FF}|\u{1F680}-\u{1F6FF}|\u{2694}-\u{2697}|\u{2699}-\u{269F}|\u{1F900}-\u{1F95F}|\u{1F980}-\u{1F9EF}|\u{1F9F0}-\u{1F9FF}]))+/u, 'g'), '_');
// }

interface GetAttrsInfoByKeysParams {
  keys: string[];
}

type GetAttrsInfoByKeysReturn = {
  key: string;
  format: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9; //	值格式 1：文本 2：数值 3：时间 4：日期 5：日期和时间 6: 单行文本 7: 多行文本 8: 整数 9: 小数
  contentRule?: {
    startDate?: string; // 4：日期
    limitDate?: string; // 4：日期
    startDateTime?: string; // 5：日期和时间
    limitDateTime?: string; // 5：日期和时间
    minLength?: number; // 6：文本  7: 多行文本
    maxLength?: number; // 6：文本  7: 多行文本
    min?: number; // 8: 整数
    max?: number; // 8: 整数
    minDecimal?: number; // 9: 小数
    maxDecimal?: number; // 9: 小数
    precision?: number; // 9: 小数
  };
  // | { startDate?: string; limitDate?: string } // 4：日期
  // | { startDateTime?: string; limitDateTime?: string } // 5：日期和时间
  // | { minLength?: number; maxLength?: number } // 6：文本  7: 多行文本
  // | { min?: number; max?: number } // 8: 整数
  // | { minDecimal?: number; maxDecimal?: number; precision?: number }; // 9: 小数
  valueConfig: {
    text?: {
      nullable: boolean;
      minLength: number;
      maxLength: number;
    };
    textArea?: {
      nullable: boolean;
      minLength: number;
      maxLength: number;
    };
    integer?: {
      nullable: boolean;
      min: number;
      max: number;
    };
    decimal?: {
      nullable: boolean;
      minDecimal: number;
      maxDecimal: number;
      precision: number;
    };
    date?: {
      nullable: boolean;
      startDate: string;
      limitDate: string;
    };
    dataTime?: {
      nullable: boolean;
      startDateTime: string;
      limitDateTime: string;
    };
    configText?: {
      nullable: boolean;
      defaultValue: string;
      minLength: number;
      maxLength: number;
    };
    configEnum?: {
      nullable: boolean;
      defaultValue: string;
      options: string[];
    };
  };
}[];

export async function getAttrsInfoByKeys({
  keys,
}: GetAttrsInfoByKeysParams): Promise<GetAttrsInfoByKeysReturn> {
  const promises: Promise<{
    ret: number;
    errCode: number;
    msg: string;
    data: GetAttrsInfoByKeysReturn[number];
  }>[] = keys.map((key: string) => {
    const params: Parameters<typeof FServiceAPI.Resource.getAttrsInfoByKey>[0] =
      {
        key: key,
      };
    return FServiceAPI.Resource.getAttrsInfoByKey(params);
  });
  const results: {
    ret: number;
    errCode: number;
    data: GetAttrsInfoByKeysReturn[number];
  }[] = await Promise.all(promises);
  // console.log(results, 'results ***************************************');

  enum FormatEnum {
    date = 4,
    dataTime = 5,
    textInput = 6,
    textArea = 7,
    integer = 8,
    decimal = 9,
  }

  return results.map((i) => {
    // | { startDate?: string; limitDate?: string } // 4：日期
    // | { startDateTime?: string; limitDateTime?: string } // 5：日期和时间
    // | { minLength?: number; maxLength?: number } // 6：文本  7: 多行文本
    // | { min?: number; max?: number } // 8: 整数
    // | { minDecimal?: number; maxDecimal?: number; precision?: number }; // 9: 小数
    // console.log(
    //   i.data,
    //   '=======================================================',
    // );
    const valueConfig: HandleData_By_Sha1_And_ResourceTypeCode_And_InheritData2_Return['systemProperties'][number]['valueConfig'] =
      {};
    const result = i.data;
    if (result) {
      if (result.format === FormatEnum.date) {
        valueConfig.date = {
          nullable: true,
          startDate: result.contentRule?.startDate || '1000-01-01',
          limitDate: result.contentRule?.limitDate || '9999-12-31',
        };
      } else if (result.format === FormatEnum.dataTime) {
        valueConfig.dataTime = {
          nullable: true,
          startDateTime:
            result.contentRule?.startDateTime || '1000-01-01 00:00:00',
          limitDateTime:
            result.contentRule?.limitDateTime || '9999-12-31 23:59:59',
        };
      } else if (result.format === FormatEnum.textInput) {
        valueConfig.text = {
          nullable: true,
          minLength: result.contentRule?.minLength || 0,
          maxLength: result.contentRule?.maxLength || 140,
        };
      } else if (result.format === FormatEnum.textArea) {
        valueConfig.textArea = {
          nullable: true,
          minLength: result.contentRule?.minLength || 0,
          maxLength: result.contentRule?.maxLength || 140,
        };
      } else if (result.format === FormatEnum.integer) {
        valueConfig.integer = {
          nullable: true,
          min: result.contentRule?.min || Number.MIN_SAFE_INTEGER,
          max: result.contentRule?.max || Number.MAX_SAFE_INTEGER,
        };
      } else if (result.format === FormatEnum.decimal) {
        valueConfig.decimal = {
          nullable: true,
          minDecimal: result.contentRule?.minDecimal || Number.MIN_SAFE_INTEGER,
          maxDecimal: result.contentRule?.maxDecimal || Number.MAX_SAFE_INTEGER,
          precision: result.contentRule?.precision || Number.MAX_SAFE_INTEGER,
        };
      }
    }
    return {
      ...result,
      valueConfig,
    };
  });
}
